/**
 * Murphy Door Configurator - Image Layer Coordinator
 * ⚡ PPD-FIRST ARCHITECTURE (Phase 2 Event-Driven Module)
 *
 * @module ImageLayerCoordinator
 * @description Coordinates image layer visibility for front/back view toggling
 *              in both BROWSING and APPLIED states. Handles 6 distinct image types
 *              per product/variant: front/back × (product/material/composite).
 *
 * @responsibilities
 * - Subscribe to view toggle events (front/back switching)
 * - Subscribe to BROWSING state selections (hover/click)
 * - Subscribe to APPLIED state selections (confirmed choices)
 * - Coordinate image layer visibility with z-index management
 * - Delegate to UI modules for actual DOM manipulation
 * - Track performance for all layer rendering operations
 *
 * @ccp-compliance
 * - Event-driven coordination ONLY (no direct module calls)
 * - PPD-First data access via conductor delegation
 * - Publishes ui:* events for all DOM updates
 * - 5-stage event lifecycle for all operations
 * - Bootstrap queuing pattern for race protection
 *
 * @author Murphy Door Development Team
 * @created 2026-02-05
 */
(function (window) {
  'use strict';

  // Namespace check
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard) - must be declared BEFORE first use
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // Centralized event constants - Pattern #19: Event Constant Usage
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  // ============================================================================
  // PERFORMANCE THRESHOLDS
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = {
    LAYER_RENDER: 50, // 50ms for layer rendering operations
    VIEW_COORDINATION: 100, // 100ms for view toggle coordination
  };

  // ============================================================================
  // Z-INDEX CONSTANTS
  // ============================================================================
  const Z_INDEX = {
    BROWSING_BASE: 1000, // BROWSING layers appear above APPLIED
    APPLIED_BASE: 500, // APPLIED layers in background
  };

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD
  // ============================================================================
  let isInitialized = false;
  let initializationPromise = null;

  // ============================================================================
  // PATTERN 9: BOOTSTRAP QUEUING PATTERN
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS
  // ============================================================================
  let cleanupHandlers = [];

  // ============================================================================
  // MODULE STATE
  // ============================================================================
  let conductor = null;
  let eventBus = null;

  // ============================================================================
  // BOOTSTRAP SUBSCRIPTION (MODULE LOAD TIME - RACE PROTECTION)
  // ============================================================================
  // Subscribe at module load time to avoid race condition with event firing before initialize()
  if (window.MurphyDoorConfigurator?.CoreEventBus) {
    const unsubBootstrap = window.MurphyDoorConfigurator.CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log(
            '[ImageLayerCoordinator] Bootstrap complete event received'
          );
        bootstrapComplete = true;

        // Process all queued calls
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[ImageLayerCoordinator] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[ImageLayerCoordinator] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);
    isDev() &&
      console.log(
        '[ImageLayerCoordinator] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // PATTERN 2: publishEvent() HELPER
  // ============================================================================
  /**
   * Centralized event publishing with error handling
   *
   * @param {string} eventType - Event type constant from CONSTANTS.EVENTS
   * @param {Object} detail - Event payload
   * @param {string} [context='operation'] - Operation context for debugging
   * @returns {boolean} Success status
   */
  function publishEvent(eventType, detail, context = 'operation') {
    try {
      if (!eventBus) {
        isDev() &&
          console.warn(
            `[ImageLayerCoordinator] EventBus not available, cannot publish ${eventType}`
          );
        return false;
      }

      const success = eventBus.publish(eventType, detail);

      if (!success) {
        isDev() &&
          console.warn(
            `[ImageLayerCoordinator] Failed to publish ${eventType}:`,
            { detail, context }
          );
      }

      return success;
    } catch (error) {
      isDev() &&
        console.error(
          `[ImageLayerCoordinator] Error publishing ${eventType}:`,
          error
        );

      // Publish failed event for critical errors
      if (eventBus) {
        eventBus.publish(
          CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_FAILED,
          {
            error: error.message,
            context,
            originalEvent: eventType,
            stack: error.stack,
          }
        );
      }

      return false;
    }
  }

  // ============================================================================
  // LAYER SUPPRESSION - Knob layers hidden when "no doors" is applied
  // ============================================================================

  /**
   * Determine whether the image layer for a given category should be suppressed.
   *
   * A knob/pull category is visually suppressed when its parent door category
   * has the "no doors" opt-out product applied.  The selection itself is preserved
   * (customers can still purchase knobs without doors) — only the visual layer
   * is hidden to avoid floating-knob artifacts.
   *
   * @param {string} categoryId - The category whose layer we want to render
   * @param {Map} appliedSelections - Current applied selections map from state
   * @returns {Promise<boolean>} true if the layer should be suppressed
   */
  async function shouldSuppressLayer(categoryId, appliedSelections) {
    // O(1) lookup — only knob categories have suppression rules
    const rule = CONSTANTS.LAYER_SUPPRESSION_DEPS[categoryId];
    if (!rule) return false;

    // Check if the parent door category has an applied selection
    const parentSelection = appliedSelections.get(rule.dependsOnCategory);
    if (!parentSelection) return false;

    // Resolve product handle via conductor (appliedSelections stores productId, not handle)
    try {
      const { product } = await requestProductDataFromConductor(
        parentSelection.productId,
        parentSelection.variantId
      );
      if (!product) return false;

      return product.handle === rule.suppressWhenProductHandle;
    } catch (error) {
      isDev() &&
        console.error(
          `[ImageLayerCoordinator] Layer suppression check failed for "${categoryId}":`,
          error
        );
      return false;
    }
  }

  // ============================================================================
  // INSET NUDGE - Knob/pull layer shifts (up or down) when paired door is inset
  // ============================================================================

  /**
   * Pure inset-ness decision for a resolved paired door — no conductor, no I/O.
   * Extracted so the field-read logic (the exact thing that silently regressed in
   * #1413) is unit-testable in isolation.
   *
   * Inset-ness is the merchant-owned custom.is_inset boolean (product-level metafield),
   * stamped onto every variant during PPD construction so it survives whichever lookup
   * map getVariantById resolves: Layer-1 add-on variants carry productIsInset; Layer-4
   * composite variants carry sourceProductIsInset. Both name the ACTUAL selected door.
   * product.is_inset is the non-composite fallback (a composite merges inset + standard
   * members, so the parent can't answer this). The boolean is authoritative and auditable;
   * we do NOT fall back to sniffing the title — a product rename silently breaks that.
   *
   * @param {object|null} product - resolved paired door product (may be a composite)
   * @param {object|null} variant - resolved selected variant
   * @returns {boolean} true when the paired door is inset
   */
  function detectInsetFromResolved(product, variant) {
    if (
      variant?.productIsInset === true ||
      variant?.sourceProductIsInset === true ||
      product?.is_inset === true
    ) {
      return true;
    }

    // Dev-only drift detector: if the door NAME says "inset" but the metafield does not,
    // the data has drifted (e.g. product renamed without setting is_inset). We do NOT act
    // on the title — this only surfaces the gap in the console for a human to fix.
    if (isDev()) {
      const doorName =
        variant?.sourceProductTitle || variant?.productTitle || product?.title || '';
      if (/\binset\b/i.test(doorName)) {
        console.warn(
          `[ImageLayerCoordinator] Door "${doorName}" is titled inset but custom.is_inset is not true — nudge skipped. Set the metafield on this product.`
        );
      }
    }
    return false;
  }

  /**
   * Compute the vertical nudge (percent of frame height) for a knob/pull layer.
   *
   * Super-app bakes the base placement of every overlay; the theme applies one
   * conditional delta: when the paired cabinet door is inset (the merchant-owned
   * custom.is_inset boolean — see detectInsetFromResolved), the knob/pull layer shifts by
   * the admin-tunable nudgePercent (negative = up, positive = down). Both upper and lower
   * knob categories are wired in INSET_NUDGE_DEPS; a category whose configured nudgePercent
   * is 0 short-circuits to 0 here (no work, no transform) — so an un-tuned lower nudge costs
   * nothing.
   *
   * @param {string} categoryId - The knob/pull category being rendered
   * @returns {Promise<number>} Nudge percent (negative = up, positive = down), 0 when no nudge applies
   */
  async function computeInsetNudge(categoryId) {
    const rule = CONSTANTS.INSET_NUDGE_DEPS?.[categoryId];
    // No rule, or a 0-value rule (un-tuned category) — nothing to nudge. Bail before
    // any conductor round-trip; the caller still clears any prior transform on 0.
    if (!rule || !rule.nudgePercent) return 0;

    try {
      const stateMaps = await requestStateFromConductor();
      const parentSelection = stateMaps?.appliedSelections?.get(rule.dependsOnCategory);
      if (!parentSelection) return 0;

      // Resolve the paired door product (appliedSelections stores IDs, not the object)
      const { product, variant } = await requestProductDataFromConductor(
        parentSelection.productId,
        parentSelection.variantId
      );
      if (!product) return 0;

      if (!detectInsetFromResolved(product, variant)) return 0;

      isDev() && console.log(
        `[ImageLayerCoordinator] Inset nudge ${rule.nudgePercent}% applied to "${categoryId}" (paired door is inset: "${variant?.sourceProductTitle || variant?.productTitle || product.title}")`
      );
      return rule.nudgePercent;
    } catch (error) {
      isDev() && console.error(
        `[ImageLayerCoordinator] Inset nudge computation failed for "${categoryId}":`,
        error
      );
      return 0;
    }
  }

  /**
   * Read the admin-owned layer shift rule for a category out of the PPD.
   *
   * Source: configurator_tab_categories.layer_shift_rule (JSON), stamped onto the
   * category classification during PPD construction. Null/absent is the normal case
   * and a no-op — a category without a rule costs one property read.
   *
   * @param {string} categoryId
   * @returns {object|null} The parsed rule, or null when absent/malformed
   */
  function requestCategoryShiftRule(categoryId) {
    if (!conductor) return null;
    try {
      return parseShiftRule(
        conductor.getCategoryClassification(categoryId)?.layerShiftRule,
        categoryId
      );
    } catch (error) {
      // getCategoryClassification throws for a category absent from the PPD.
      isDev() &&
        console.warn(
          `[ImageLayerCoordinator] layer_shift_rule lookup failed for "${categoryId}" — ignored:`,
          error
        );
      return null;
    }
  }

  /**
   * Pure parse + validate for a raw layer_shift_rule metafield value.
   *
   * Split out of requestCategoryShiftRule for the same reason resolveShiftFromRule is
   * split out of computeAdminLayerShift: this holds all the tolerance rules (unparsed
   * JSON string, unpopulated field, malformed shape) and is the part that would silently
   * drop a merchant's rule if it regressed. The conductor read left behind is a single
   * property access, untested by the same standard as requestCategoryMediaZIndex.
   *
   * @param {*} raw - the metafield value as it arrives from the PPD
   * @param {string} categoryId - for the dev-mode warning only
   * @returns {object|null} validated rule, or null when absent/malformed
   */
  function parseShiftRule(raw, categoryId) {
    if (!raw) return null;
    let rule = raw;
    // Shopify json metafields arrive parsed via Liquid `| json`, but tolerate a
    // string in case a caller hands us the raw metafield value.
    if (typeof raw === 'string') {
      try {
        rule = JSON.parse(raw);
      } catch (error) {
        isDev() &&
          console.warn(
            `[ImageLayerCoordinator] layer_shift_rule for "${categoryId}" is not valid JSON — ignored:`,
            error
          );
        return null;
      }
    }
    if (!rule || typeof rule !== 'object') return null;
    if (!rule.dependsOnCategory || !Array.isArray(rule.cases)) {
      isDev() &&
        console.warn(
          `[ImageLayerCoordinator] layer_shift_rule for "${categoryId}" is missing dependsOnCategory or cases — ignored.`
        );
      return null;
    }
    return rule;
  }

  /**
   * Pure rule → shift resolution. No conductor, no I/O.
   *
   * Extracted for the same reason detectInsetFromResolved was: this is the part with all
   * the edge cases (case-insensitive matching, unmatched value, non-numeric percent,
   * clamping), and it is the part that would silently mis-shift a layer if it regressed.
   *
   * @param {object|null} rule - a validated layer_shift_rule
   * @param {*} rawValue - the merchant-owned field value read off the parent selection
   * @returns {{axis: string, percent: number}} percent 0 when no case matches
   */
  function resolveShiftFromRule(rule, rawValue) {
    const axis = rule?.axis === 'y' ? 'y' : 'x';
    const NONE = { axis, percent: 0 };
    if (!rule || !Array.isArray(rule.cases)) return NONE;
    if (rawValue === null || rawValue === undefined || rawValue === '') return NONE;

    const value = String(rawValue).trim().toLowerCase();
    const hit = rule.cases.find(
      (c) => c && String(c.equals).trim().toLowerCase() === value
    );
    // No matching case is the normal "other side" path, not an error.
    if (!hit) return NONE;

    const parsed = Number(hit.shiftPercent);
    if (!Number.isFinite(parsed)) return NONE;
    // Clamp to one frame either way; beyond that is never intended and would park the
    // layer off-canvas where a merchant could not see the mistake.
    return { axis, percent: Math.max(-100, Math.min(100, parsed)) };
  }

  /**
   * Pure product-scope check for a layer_shift_rule.
   *
   * A rule lives on the CATEGORY, but a category can hold several unrelated products
   * (Locking Systems: Book Latch, Mag Lock + Keypad, Mag Lock + Fob, ...). An unscoped
   * rule — no `appliesToProductId` — applies to every product in the category, matching
   * the original shipped behavior. A scoped rule only applies when the variant CURRENTLY
   * being rendered belongs to that one product.
   *
   * @param {object|null} rule - a validated layer_shift_rule
   * @param {string|number} currentProductId - productId of the variant being rendered
   * @returns {boolean}
   */
  function ruleAppliesToProduct(rule, currentProductId) {
    if (!rule?.appliesToProductId) return true;
    return String(rule.appliesToProductId) === String(currentProductId);
  }

  /**
   * Evaluate a category's admin layer shift rule against the CURRENT selection of the
   * parent category it depends on.
   *
   * The generalized sibling of computeInsetNudge: same shape (parent category →
   * merchant-owned field on the resolved selection → percent-of-frame delta), but the
   * axis and trigger value are admin-authored instead of hardcoded. Shipped for the
   * Book Latch, whose book sits against the latch-side wall of the shelf and so must
   * move to the far side when the door is hung on the other hand.
   *
   * The rule lives on the CATEGORY (e.g. "Locking Systems"), but a category can hold
   * several unrelated products (Book Latch, Mag Lock + Keypad, Mag Lock + Fob, ...).
   * An optional `appliesToProductId` scopes the rule to the one product it was authored
   * for, so a shift tuned for the Book Latch's book graphic doesn't also fling an
   * unrelated product's layer off-canvas. Absent `appliesToProductId` = apply to every
   * product in the category (the original, unscoped behavior).
   *
   * Reads a merchant-owned field (default `door_hand`) off the resolved parent variant —
   * NEVER the variant title. Titles rename silently; see detectInsetFromResolved for the
   * same rule and the drift detector that enforces it.
   *
   * @param {string} categoryId - The dependent category being rendered
   * @param {string|number} [currentProductId] - The productId of the variant currently
   *   being rendered for this category. Required to honor `appliesToProductId` scoping.
   * @returns {Promise<{axis: string, percent: number}>} axis 'x'|'y'; percent 0 = no shift
   */
  async function computeAdminLayerShift(categoryId, currentProductId) {
    const NONE = { axis: 'x', percent: 0 };
    const rule = requestCategoryShiftRule(categoryId);
    if (!rule) return NONE;
    if (!ruleAppliesToProduct(rule, currentProductId)) return NONE;

    try {
      const stateMaps = await requestStateFromConductor();
      const parentSelection = stateMaps?.appliedSelections?.get(
        rule.dependsOnCategory
      );
      if (!parentSelection) return NONE;

      const { product, variant } = await requestProductDataFromConductor(
        parentSelection.productId,
        parentSelection.variantId
      );
      if (!variant && !product) return NONE;

      // Variant-level first (handing is per-variant), then a product-level fallback so
      // the same rule shape also works for a product-scoped flag such as is_inset.
      const field = rule.sourceField || 'door_hand';
      const rawValue = variant?.[field] ?? product?.[field] ?? null;
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        isDev() &&
          console.warn(
            `[ImageLayerCoordinator] layer_shift_rule for "${categoryId}" depends on "${rule.dependsOnCategory}".${field}, but the applied selection has no such value — no shift. Populate that metafield on the variant.`
          );
        return NONE;
      }

      const { axis, percent } = resolveShiftFromRule(rule, rawValue);
      if (!percent) return NONE;

      isDev() &&
        console.log(
          `[ImageLayerCoordinator] Admin layer shift ${percent}% on ${axis} applied to "${categoryId}" (${rule.dependsOnCategory}.${field}="${rawValue}")`
        );
      return { axis, percent };
    } catch (error) {
      isDev() &&
        console.error(
          `[ImageLayerCoordinator] Admin layer shift failed for "${categoryId}":`,
          error
        );
      return NONE;
    }
  }

  /**
   * Combined per-layer shift, in percent of the frame on each axis.
   *
   * Two independent contributors, deliberately kept separate:
   *   y      ← computeInsetNudge       (hardcoded knob/pull rule, section-tunable amount)
   *   x or y ← computeAdminLayerShift  (admin-authored metaobject rule)
   * An admin rule on the y axis ADDS to the inset nudge rather than replacing it, so a
   * layer could carry both without one silently winning.
   *
   * @param {string} categoryId
   * @param {string|number} [currentProductId] - passed through to computeAdminLayerShift
   *   so a product-scoped admin rule only fires for the product it was authored for
   * @returns {Promise<{x: number, y: number}>}
   */
  async function computeLayerShift(categoryId, currentProductId) {
    const [insetY, admin] = await Promise.all([
      computeInsetNudge(categoryId),
      computeAdminLayerShift(categoryId, currentProductId),
    ]);
    return {
      x: admin.axis === 'x' ? admin.percent : 0,
      y: (insetY || 0) + (admin.axis === 'y' ? admin.percent : 0),
    };
  }

  // ============================================================================
  // PATTERN 3: 5-STAGE EVENT LIFECYCLE HANDLERS
  // ============================================================================

  /**
   * Handle view toggle event (front ↔ back switching)
   *
   * @param {Object} eventData - View toggle event data
   * @param {string} eventData.view - New view ('front' | 'back')
   * @param {string} [eventData.categoryId] - Optional category filter
   * @returns {Promise<void>}
   */
  async function handleViewToggled(eventData) {
    const operationId = `view_toggle_${Date.now()}`;
    const startTime = performance.now();

    try {
      // STAGE 1: START
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_STARTED,
        {
          operationId,
          operation: 'view_toggled',
          view: eventData.view,
          categoryId: eventData.categoryId,
          timestamp: Date.now(),
        },
        'handleViewToggled'
      );

      // STAGE 2: PROGRESS - Validation
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_PROGRESS,
        {
          operationId,
          stage: 'validation',
          progress: 0.2,
        },
        'handleViewToggled'
      );

      // STAGE 3: VALIDATION
      const validation = validateViewToggleData(eventData);
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_VALIDATION,
        {
          operationId,
          isValid: validation.isValid,
          errors: validation.errors,
          warnings: validation.warnings,
        },
        'handleViewToggled'
      );

      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // STAGE 2b: PROGRESS - Coordination
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_PROGRESS,
        {
          operationId,
          stage: 'coordination',
          progress: 0.5,
        },
        'handleViewToggled'
      );

      // Get current state from conductor
      const stateMaps = await requestStateFromConductor();

      // Coordinate layer updates for BOTH views (ensures persistence)
      // Check browsingSelections Map
      if (
        stateMaps.browsingSelections &&
        stateMaps.browsingSelections.size > 0
      ) {
        for (const [
          categoryId,
          selectionData,
        ] of stateMaps.browsingSelections.entries()) {
          // Re-render BOTH views to maintain persistence
          await renderBrowsingLayer(categoryId, 'front', selectionData);
          await renderBrowsingLayer(categoryId, 'back', selectionData);
        }
      }

      // Check appliedSelections Map
      if (stateMaps.appliedSelections && stateMaps.appliedSelections.size > 0) {
        for (const [
          categoryId,
          selectionData,
        ] of stateMaps.appliedSelections.entries()) {
          // Re-render BOTH views to maintain persistence
          await renderAppliedLayer(categoryId, 'front', selectionData);
          await renderAppliedLayer(categoryId, 'back', selectionData);
        }
      }

      // STAGE 4: PERFORMANCE TRACKING (PATTERN 4)
      const duration = performance.now() - startTime;
      const threshold = PERFORMANCE_THRESHOLDS.VIEW_COORDINATION;

      if (duration > threshold) {
        isDev() &&
          console.warn(
            `[ImageLayerCoordinator] ⚠️ View toggle exceeded threshold: ${duration.toFixed(
              2
            )}ms > ${threshold}ms`
          );
        publishEvent(
          CONSTANTS.EVENTS.PERFORMANCE_IMAGE_LAYER_OPERATION_SLOW,
          {
            operationId,
            operation: 'view_toggled',
            duration: duration.toFixed(2),
            threshold,
          },
          'handleViewToggled'
        );
      }

      // STAGE 5: SUCCESS
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_COMPLETED,
        {
          operationId,
          operation: 'view_toggled',
          view: eventData.view,
          duration: duration.toFixed(2),
          timestamp: Date.now(),
        },
        'handleViewToggled'
      );
    } catch (error) {
      isDev() &&
        console.error('[ImageLayerCoordinator] View toggle failed:', error);

      // STAGE 5: FAILED
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_FAILED,
        {
          operationId,
          operation: 'view_toggled',
          error: error.message,
          stack: error.stack,
          timestamp: Date.now(),
        },
        'handleViewToggled'
      );

      throw error;
    }
  }

  /**
   * Handle BROWSING state selection (hover/click preview)
   *
   * @param {Object} eventData - Selection event data
   * @param {string} eventData.categoryId - Category ID
   * @param {string} eventData.productId - Product ID
   * @param {string} [eventData.variantId] - Variant ID (for multi-variant)
   * @returns {Promise<void>}
   */
  async function handleBrowsingSelection(eventData) {
    const operationId = `browsing_${Date.now()}`;
    const startTime = performance.now();

    try {
      // STAGE 1: START
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_STARTED,
        {
          operationId,
          operation: 'browsing_selection',
          categoryId: eventData.categoryId,
          productId: eventData.productId,
          variantId: eventData.variantId,
          timestamp: Date.now(),
        },
        'handleBrowsingSelection'
      );

      // STAGE 2: PROGRESS - Validation
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_PROGRESS,
        {
          operationId,
          stage: 'validation',
          progress: 0.2,
        },
        'handleBrowsingSelection'
      );

      // STAGE 3: VALIDATION
      const validation = validateSelectionData(eventData);
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_VALIDATION,
        {
          operationId,
          isValid: validation.isValid,
          errors: validation.errors,
          warnings: validation.warnings,
        },
        'handleBrowsingSelection'
      );

      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // STAGE 2b: PROGRESS - Rendering
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_PROGRESS,
        {
          operationId,
          stage: 'rendering',
          progress: 0.5,
        },
        'handleBrowsingSelection'
      );

      // Render BROWSING layers for BOTH views (ensures persistence)
      const browsingPaintedFront = await renderBrowsingLayer(
        eventData.categoryId,
        'front',
        eventData
      );
      const browsingPaintedBack = await renderBrowsingLayer(
        eventData.categoryId,
        'back',
        eventData
      );

      // When the browsed selection has NO image in EITHER view (e.g. clicking Standard backing,
      // whose overlay was removed once the screws were baked into the base render), clear any
      // previously-painted browsing overlay for this category so it doesn't persist. Firing only
      // on the both-empty case spares single-view categories (front-only knobs/casing/cabinets)
      // whose opposite view is legitimately empty.
      if (!browsingPaintedFront && !browsingPaintedBack) {
        publishEvent(
          CONSTANTS.EVENTS.UI_BROWSING_LAYER_REMOVE,
          {
            categoryId: eventData.categoryId,
            source: 'image_layer_coordinator.empty_browsing_selection',
            timestamp: Date.now(),
          },
          'handleBrowsingSelection'
        );
      }

      // ✨ AUTO-TOGGLE: Check if we need to switch views
      const currentView = await requestCurrentViewFromConductor();
      const { product, variant } = await requestProductDataFromConductor(
        eventData.productId,
        eventData.variantId
      );

      const targetView = mayAutoToggleForCategory(eventData.categoryId)
        ? detectAutoToggleNeeded(product, variant, currentView)
        : null;

      if (targetView) {
        publishEvent(
          CONSTANTS.EVENTS.UI_AUTO_TOGGLE_VIEW,
          {
            currentView,
            targetView,
            reason: `only_${targetView}_image_available`,
            categoryId: eventData.categoryId,
            productId: eventData.productId,
            variantId: eventData.variantId,
          },
          'handleBrowsingSelection'
        );
        isDev() &&
          console.log(
            `[ImageLayerCoordinator] Auto-toggle requested: ${currentView} → ${targetView}`
          );
      }

      // STAGE 4: PERFORMANCE TRACKING (PATTERN 4)
      const duration = performance.now() - startTime;
      const threshold = PERFORMANCE_THRESHOLDS.LAYER_RENDER;

      if (duration > threshold) {
        isDev() &&
          console.warn(
            `[ImageLayerCoordinator] ⚠️ BROWSING render exceeded threshold: ${duration.toFixed(
              2
            )}ms > ${threshold}ms`
          );
        publishEvent(
          CONSTANTS.EVENTS.PERFORMANCE_IMAGE_LAYER_OPERATION_SLOW,
          {
            operationId,
            operation: 'browsing_selection',
            duration: duration.toFixed(2),
            threshold,
          },
          'handleBrowsingSelection'
        );
      }

      // STAGE 5: SUCCESS
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_COMPLETED,
        {
          operationId,
          operation: 'browsing_selection',
          categoryId: eventData.categoryId,
          duration: duration.toFixed(2),
          timestamp: Date.now(),
        },
        'handleBrowsingSelection'
      );
    } catch (error) {
      isDev() &&
        console.error(
          '[ImageLayerCoordinator] BROWSING selection failed:',
          error
        );

      // STAGE 5: FAILED
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_FAILED,
        {
          operationId,
          operation: 'browsing_selection',
          error: error.message,
          stack: error.stack,
          timestamp: Date.now(),
        },
        'handleBrowsingSelection'
      );

      throw error;
    }
  }

  /**
   * Handle APPLIED state selection (confirmed choice)
   *
   * This function MUST be idempotent — it can be called from multiple event paths:
   * 1. SELECTION_APPLIED (primary path via state manager)
   * 2. FORBIDDEN_AUTO_APPLY_COMPLETED (safety-net for auto-applied composite products)
   * Re-rendering an already-correct layer with the same images/z-index is a safe no-op.
   *
   * @param {Object} eventData - Selection event data
   * @param {string} eventData.categoryId - Category ID
   * @param {string} eventData.productId - Product ID
   * @param {string} [eventData.variantId] - Variant ID (for multi-variant)
   * @returns {Promise<void>}
   */
  async function handleAppliedSelection(eventData) {
    const operationId = `applied_${Date.now()}`;
    const startTime = performance.now();

    try {
      // STAGE 1: START
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_STARTED,
        {
          operationId,
          operation: 'applied_selection',
          categoryId: eventData.categoryId,
          productId: eventData.productId,
          variantId: eventData.variantId,
          timestamp: Date.now(),
        },
        'handleAppliedSelection'
      );

      // STAGE 2: PROGRESS - Validation
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_PROGRESS,
        {
          operationId,
          stage: 'validation',
          progress: 0.2,
        },
        'handleAppliedSelection'
      );

      // STAGE 3: VALIDATION
      const validation = validateSelectionData(eventData);
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_VALIDATION,
        {
          operationId,
          isValid: validation.isValid,
          errors: validation.errors,
          warnings: validation.warnings,
        },
        'handleAppliedSelection'
      );

      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // STAGE 2b: PROGRESS - Rendering
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_PROGRESS,
        {
          operationId,
          stage: 'rendering',
          progress: 0.5,
        },
        'handleAppliedSelection'
      );

      // ✅ PERFORMANCE: Fetch product data ONCE and pass to both render calls + auto-toggle
      // Previously fetched 3x (once per renderAppliedLayer + once for auto-toggle detection)
      const { product, variant } = await requestProductDataFromConductor(
        eventData.productId,
        eventData.variantId
      );

      // Render APPLIED layers for BOTH views (ensures persistence across view toggling)
      const appliedPaintedFront = await renderAppliedLayer(
        eventData.categoryId,
        'front',
        eventData,
        product,
        variant
      );
      const appliedPaintedBack = await renderAppliedLayer(
        eventData.categoryId,
        'back',
        eventData,
        product,
        variant
      );

      // When the applied selection has NO image in EITHER view (e.g. Standard backing, whose
      // overlay was removed once the screws were baked into the base render), clear any
      // previously-painted layer for this category so the prior Flat/Mirror overlay doesn't
      // persist. Firing only on the both-empty case spares single-view categories (front-only
      // knobs/casing/cabinets) whose opposite view is legitimately empty.
      if (!appliedPaintedFront && !appliedPaintedBack) {
        publishEvent(
          CONSTANTS.EVENTS.UI_APPLIED_LAYER_REMOVE,
          {
            categoryId: eventData.categoryId,
            source: 'image_layer_coordinator.empty_applied_selection',
            timestamp: Date.now(),
          },
          'handleAppliedSelection'
        );
      }

      // 🚫 LAYER SUPPRESSION: When a door category is applied with "no doors",
      // remove any existing knob layers for the dependent knob category.
      // Iterate all suppression rules to find any where THIS category is the parent.
      const suppressionDeps = CONSTANTS.LAYER_SUPPRESSION_DEPS;
      for (const [dependentCategory, rule] of Object.entries(suppressionDeps)) {
        if (rule.dependsOnCategory === eventData.categoryId) {
          // This applied category is a parent door category — check if "no doors" was selected
          if (product && product.handle === rule.suppressWhenProductHandle) {
            isDev() &&
              console.log(
                `[ImageLayerCoordinator] "No doors" applied for "${eventData.categoryId}" — removing knob layers for "${dependentCategory}"`
              );

            // Remove both applied and browsing layers for the dependent knob category
            publishEvent(
              CONSTANTS.EVENTS.UI_APPLIED_LAYER_REMOVE,
              {
                categoryId: dependentCategory,
                reason: 'layer_suppression_no_doors',
                parentCategoryId: eventData.categoryId,
              },
              'handleAppliedSelection'
            );
            publishEvent(
              CONSTANTS.EVENTS.UI_BROWSING_LAYER_REMOVE,
              {
                categoryId: dependentCategory,
                reason: 'layer_suppression_no_doors',
                parentCategoryId: eventData.categoryId,
              },
              'handleAppliedSelection'
            );
            publishEvent(
              CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_SUPPRESSED,
              {
                categoryId: dependentCategory,
                layerType: 'both',
                reason: 'parent_door_applied_no_doors',
                parentCategoryId: eventData.categoryId,
                parentProductHandle: product.handle,
                timestamp: Date.now(),
              },
              'handleAppliedSelection'
            );
          }
        }
      }

      // 🔧 LAYER SHIFT REACTIVITY: when a parent category is (re)applied, re-render any
      // already-applied dependent layer so its shift re-evaluates against the new parent.
      // Two rule sources feed ONE dependent list:
      //   • CONSTANTS.INSET_NUDGE_DEPS — knob/pull vertical nudge (parent = cabinet door)
      //   • layer_shift_rule (PPD)     — admin-authored (Book Latch book ← Door Handing)
      //
      // Scope is deliberately the APPLIED set: a layer that is not applied has nothing to
      // re-render, so appliedSelections is both the correct and the cheapest place to look
      // for admin-rule dependents. There is no conductor API that enumerates every known
      // category, and adding one purely to scan for rules would be strictly worse.
      //
      // renderAppliedLayer self-suppresses when the parent door is "no doors", so this
      // cannot resurrect a suppressed knob layer.
      try {
        const shiftState = await requestStateFromConductor();
        const applied = shiftState?.appliedSelections;

        if (applied && applied.size) {
          const dependents = new Set();

          for (const [dependent, rule] of Object.entries(
            CONSTANTS.INSET_NUDGE_DEPS
          )) {
            // Un-tuned (0%) rules have nothing to re-nudge — skip the re-render.
            if (rule.nudgePercent && rule.dependsOnCategory === eventData.categoryId) {
              dependents.add(dependent);
            }
          }
          for (const dependent of applied.keys()) {
            const rule = requestCategoryShiftRule(dependent);
            if (rule && rule.dependsOnCategory === eventData.categoryId) {
              dependents.add(dependent);
            }
          }

          for (const dependent of dependents) {
            const selection = applied.get(dependent);
            if (!selection || !selection.productId) continue;
            isDev() &&
              console.log(
                `[ImageLayerCoordinator] "${eventData.categoryId}" applied — re-rendering "${dependent}" for layer-shift re-eval`
              );
            await renderAppliedLayer(dependent, 'front', selection);
            await renderAppliedLayer(dependent, 'back', selection);
          }
        }
      } catch (shiftError) {
        // Isolated: the shift re-eval is cosmetic and must NOT fail the primary apply
        // operation (renderAppliedLayer re-throws on error).
        isDev() &&
          console.warn(
            '[ImageLayerCoordinator] Layer-shift re-render failed (non-fatal, degrading to no re-shift):',
            shiftError
          );
      }

      // ✨ AUTO-TOGGLE: Check if we need to switch views
      const currentView = await requestCurrentViewFromConductor();

      const targetView = mayAutoToggleForCategory(eventData.categoryId)
        ? detectAutoToggleNeeded(product, variant, currentView)
        : null;

      if (targetView) {
        publishEvent(
          CONSTANTS.EVENTS.UI_AUTO_TOGGLE_VIEW,
          {
            currentView,
            targetView,
            reason: `only_${targetView}_image_available`,
            categoryId: eventData.categoryId,
            productId: eventData.productId,
            variantId: eventData.variantId,
          },
          'handleAppliedSelection'
        );
        isDev() &&
          console.log(
            `[ImageLayerCoordinator] Auto-toggle requested: ${currentView} → ${targetView}`
          );
      }

      // STAGE 4: PERFORMANCE TRACKING (PATTERN 4)
      const duration = performance.now() - startTime;
      const threshold = PERFORMANCE_THRESHOLDS.LAYER_RENDER;

      if (duration > threshold) {
        isDev() &&
          console.warn(
            `[ImageLayerCoordinator] ⚠️ APPLIED render exceeded threshold: ${duration.toFixed(
              2
            )}ms > ${threshold}ms`
          );
        publishEvent(
          CONSTANTS.EVENTS.PERFORMANCE_IMAGE_LAYER_OPERATION_SLOW,
          {
            operationId,
            operation: 'applied_selection',
            duration: duration.toFixed(2),
            threshold,
          },
          'handleAppliedSelection'
        );
      }

      // STAGE 5: SUCCESS
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_COMPLETED,
        {
          operationId,
          operation: 'applied_selection',
          categoryId: eventData.categoryId,
          duration: duration.toFixed(2),
          timestamp: Date.now(),
        },
        'handleAppliedSelection'
      );
    } catch (error) {
      isDev() &&
        console.error(
          '[ImageLayerCoordinator] APPLIED selection failed:',
          error
        );

      // STAGE 5: FAILED
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_FAILED,
        {
          operationId,
          operation: 'applied_selection',
          error: error.message,
          stack: error.stack,
          timestamp: Date.now(),
        },
        'handleAppliedSelection'
      );

      throw error;
    }
  }

  /**
   * Handle UNAPPLY completed — remove applied image layers for the unapplied category.
   *
   * When a user clicks × to unapply a selection, the conductor clears state and publishes
   * SELECTION_USER_UNAPPLY_COMPLETED. This handler bridges that event to the
   * UI_APPLIED_LAYER_REMOVE event that media-gallery.js subscribes to for DOM cleanup.
   *
   * Without this handler, applied layers persist as ghost DOM elements after unapply.
   *
   * @param {Object} eventData - Unapply completion event data
   * @param {string} eventData.categoryId - Category whose applied layers should be removed
   * @returns {void}
   */
  function handleUnapplyCompleted(eventData) {
    const { categoryId } = eventData;

    if (!categoryId) {
      isDev() &&
        console.warn(
          '[ImageLayerCoordinator] UNAPPLY_COMPLETED missing categoryId, skipping layer removal'
        );
      return;
    }

    isDev() &&
      console.log(
        '[ImageLayerCoordinator] Removing applied + browsing layers after unapply',
        { categoryId }
      );

    // Publish both UI_APPLIED_LAYER_REMOVE and UI_BROWSING_LAYER_REMOVE so media-gallery.js
    // removes all DOM layers (applied + any ghost browsing/hover layers that persisted).
    // NOTE: Object.freeze() not needed - CoreEventBus.publish() freezes centrally (Pattern #18 DRY)
    const basePayload = {
      categoryId,
      source: 'image_layer_coordinator.unapply',
    };
    publishEvent(
      CONSTANTS.EVENTS.UI_APPLIED_LAYER_REMOVE,
      { ...basePayload, timestamp: Date.now() },
      'handleUnapplyCompleted'
    );
    publishEvent(
      CONSTANTS.EVENTS.UI_BROWSING_LAYER_REMOVE,
      { ...basePayload, timestamp: Date.now() },
      'handleUnapplyCompleted'
    );
  }

  /**
   * Safety-net for FORBIDDEN_AUTO_APPLY_COMPLETED — reads committed appliedSelections state
   * instead of relying on event payload IDs.
   *
   * The primary path (SELECTION_APPLIED → handleAppliedSelection) calls
   * requestProductDataFromConductor(productId, variantId). For auto-applied composite shelf
   * products, productId is a synthetic string like "composite-bottom-pocket-standard-shelves"
   * that intermittently fails PPD map lookups. This safety-net reads from committed state
   * (guaranteed after FORBIDDEN_AUTO_APPLY_COMPLETED fires) and re-renders the layer.
   *
   * @param {string} targetCategoryId - Category ID to re-render from committed state
   * @returns {Promise<void>}
   */
  async function handleForbiddenAutoApplyFromCommittedState(targetCategoryId) {
    try {
      const stateMaps = await requestStateFromConductor();
      if (
        !stateMaps.appliedSelections ||
        stateMaps.appliedSelections.size === 0
      )
        return;
      const selectionData = stateMaps.appliedSelections.get(targetCategoryId);
      if (selectionData && selectionData.productId && selectionData.variantId) {
        isDev() &&
          console.log(
            '[ImageLayerCoordinator] Safety-net: re-rendering from committed state after FORBIDDEN_AUTO_APPLY_COMPLETED',
            {
              targetCategoryId,
              productId: selectionData.productId,
              variantId: selectionData.variantId,
            }
          );
        await handleAppliedSelection({
          categoryId: targetCategoryId,
          productId: selectionData.productId,
          variantId: selectionData.variantId,
        });
      }
    } catch (error) {
      isDev() &&
        console.error(
          '[ImageLayerCoordinator] Forbidden auto-apply safety-net failed:',
          error
        );
    }
  }

  // ============================================================================
  // RENDERING COORDINATION (PPD-FIRST PATTERN 5 & 6)
  // ============================================================================

  /**
   * Render BROWSING layer for given category/view
   * PATTERN 4: Performance tracking
   * PATTERN 5 & 6: PPD-First O(1) lookups via conductor
   *
   * @param {string} categoryId - Category ID
   * @param {string} view - View type ('front' | 'back')
   * @param {Object} selectionData - Selection data
   * @returns {Promise<boolean>} true = layer painted for this view OR suppression handled it;
   *   false = no image for this view. LOAD-BEARING: handleBrowsingSelection tears down the
   *   category layer only when BOTH views return false. Rejects (throws) on render error — never
   *   resolve a bare/undefined value here, or a both-views-empty misread would wipe a valid layer.
   */
  async function renderBrowsingLayer(categoryId, view, selectionData) {
    const startTime = performance.now();

    try {
      // Layer suppression: skip knob layers when parent door category has "no doors" applied
      if (CONSTANTS.LAYER_SUPPRESSION_DEPS[categoryId]) {
        const stateMaps = await requestStateFromConductor();
        if (
          stateMaps.appliedSelections &&
          (await shouldSuppressLayer(categoryId, stateMaps.appliedSelections))
        ) {
          isDev() &&
            console.log(
              `[ImageLayerCoordinator] BROWSING layer suppressed for "${categoryId}" (no doors applied)`
            );
          publishEvent(
            CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_SUPPRESSED,
            {
              categoryId,
              view,
              layerType: 'browsing',
              reason: 'parent_door_category_has_no_doors',
              timestamp: Date.now(),
            },
            'renderBrowsingLayer'
          );
          // Suppression is handled by its own event — treat as "handled" so the caller's
          // both-views-empty teardown does not also fire for this category.
          return true;
        }
      }

      // PATTERN 5 & 6: Request data from conductor (PPD-First)
      const { product, variant } = await requestProductDataFromConductor(
        selectionData.productId,
        selectionData.variantId
      );

      // Get images for current view (single source of truth)
      const images = getImagesForView(product, variant, view);

      if (!images || images.length === 0) {
        isDev() &&
          console.warn(
            `[ImageLayerCoordinator] No images for ${view} view (browsing):`,
            { categoryId, productId: selectionData.productId }
          );
        // Signal "no image in THIS view" to the caller. Do NOT fire a category-scoped teardown
        // here: a single-view category (front-only knob/casing/cabinet) legitimately has an
        // empty opposite view, and a category-scoped remove would wipe the view that painted.
        // handleBrowsingSelection fires the remove only when BOTH views come back empty.
        return false;
      }

      // Calculate z-index from PPD category metadata (base + mediaZIndex)
      const computedZIndex = calculateLayerZIndex('browsing', categoryId);

      // Layer shift: inset knob/pull nudge (y) + admin layer_shift_rule (x or y).
      // {x:0,y:0} for the overwhelming majority of layers — see computeLayerShift.
      const shiftPercent = await computeLayerShift(
        categoryId,
        selectionData.productId
      );

      // Publish UI event to show BROWSING layer
      // NOTE: Object.freeze() not needed - CoreEventBus.publish() freezes centrally (Pattern #18 DRY)
      publishEvent(
        CONSTANTS.EVENTS.UI_BROWSING_LAYER_SHOW,
        {
          categoryId,
          view,
          images,
          zIndex: computedZIndex,
          stackOrder: requestCategoryStackOrder(categoryId),
          shiftPercent,
          productId: selectionData.productId,
          variantId: selectionData.variantId,
        },
        'renderBrowsingLayer'
      );

      // Performance tracking
      const duration = performance.now() - startTime;
      if (duration > PERFORMANCE_THRESHOLDS.LAYER_RENDER) {
        isDev() &&
          console.warn(
            `[ImageLayerCoordinator] BROWSING render slow: ${duration.toFixed(2)}ms`
          );
      }
      return true; // painted this view
    } catch (error) {
      isDev() &&
        console.error('[ImageLayerCoordinator] BROWSING render failed:', error);
      throw error;
    }
  }

  /**
   * Render APPLIED layer for given category/view
   * PATTERN 4: Performance tracking
   * PATTERN 5 & 6: PPD-First O(1) lookups via conductor
   *
   * @param {string} categoryId - Category ID
   * @param {string} view - View type ('front' | 'back')
   * @param {Object} selectionData - Selection data
   * @param {Object} [prefetchedProduct] - Pre-fetched product (avoids redundant conductor lookup)
   * @param {Object} [prefetchedVariant] - Pre-fetched variant (avoids redundant conductor lookup)
   * @returns {Promise<boolean>} true = layer painted for this view OR suppression handled it;
   *   false = no image for this view. LOAD-BEARING: handleAppliedSelection tears down the category
   *   layer only when BOTH views return false. Rejects (throws) on render error — never resolve a
   *   bare/undefined value here, or a both-views-empty misread would wipe a valid layer.
   */
  async function renderAppliedLayer(
    categoryId,
    view,
    selectionData,
    prefetchedProduct,
    prefetchedVariant
  ) {
    const startTime = performance.now();

    try {
      // Layer suppression: skip knob layers when parent door category has "no doors" applied
      if (CONSTANTS.LAYER_SUPPRESSION_DEPS[categoryId]) {
        const stateMaps = await requestStateFromConductor();
        if (
          stateMaps.appliedSelections &&
          (await shouldSuppressLayer(categoryId, stateMaps.appliedSelections))
        ) {
          isDev() &&
            console.log(
              `[ImageLayerCoordinator] APPLIED layer suppressed for "${categoryId}" (no doors applied)`
            );
          publishEvent(
            CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_SUPPRESSED,
            {
              categoryId,
              view,
              layerType: 'applied',
              reason: 'parent_door_category_has_no_doors',
              timestamp: Date.now(),
            },
            'renderAppliedLayer'
          );
          // Suppression is handled by its own event — treat as "handled" so the caller's
          // both-views-empty teardown does not also fire for this category.
          return true;
        }
      }

      // PATTERN 5 & 6: Use pre-fetched data or request from conductor (PPD-First)
      let product = prefetchedProduct;
      let variant = prefetchedVariant;
      if (!product && !variant) {
        const data = await requestProductDataFromConductor(
          selectionData.productId,
          selectionData.variantId
        );
        product = data.product;
        variant = data.variant;
      }

      // Get images for current view (single source of truth)
      const images = getImagesForView(product, variant, view);

      if (!images || images.length === 0) {
        isDev() &&
          console.warn(
            `[ImageLayerCoordinator] No images for ${view} view (applied):`,
            { categoryId, productId: selectionData.productId }
          );
        // Signal "no image in THIS view" to the caller. Do NOT fire a category-scoped teardown
        // here: a single-view category (front-only knob/casing/cabinet) legitimately has an
        // empty opposite view, and a category-scoped remove would wipe the view that painted.
        // handleAppliedSelection fires the remove only when BOTH views come back empty.
        return false;
      }

      // Calculate z-index from PPD category metadata (base + mediaZIndex)
      const computedZIndex = calculateLayerZIndex('applied', categoryId);

      // Layer shift: inset knob/pull nudge (y) + admin layer_shift_rule (x or y).
      // {x:0,y:0} for the overwhelming majority of layers — see computeLayerShift.
      const shiftPercent = await computeLayerShift(
        categoryId,
        selectionData.productId
      );

      // Publish UI event to update APPLIED layer
      // NOTE: Object.freeze() not needed - CoreEventBus.publish() freezes centrally (Pattern #18 DRY)
      publishEvent(
        CONSTANTS.EVENTS.UI_APPLIED_LAYER_UPDATE,
        {
          categoryId,
          view,
          images,
          zIndex: computedZIndex,
          stackOrder: requestCategoryStackOrder(categoryId),
          shiftPercent,
          productId: selectionData.productId,
          variantId: selectionData.variantId,
        },
        'renderAppliedLayer'
      );

      // Performance tracking
      const duration = performance.now() - startTime;
      if (duration > PERFORMANCE_THRESHOLDS.LAYER_RENDER) {
        isDev() &&
          console.warn(
            `[ImageLayerCoordinator] APPLIED render slow: ${duration.toFixed(2)}ms`
          );
      }
      return true; // painted this view
    } catch (error) {
      isDev() &&
        console.error('[ImageLayerCoordinator] APPLIED render failed:', error);
      throw error;
    }
  }

  // ============================================================================
  // DATA ACCESS (PATTERN 5 & 6: PPD-FIRST VIA CONDUCTOR)
  // ============================================================================

  /**
   * Get images for specific view (single source of truth)
   * Determines which image type to use based on product/variant structure
   *
   * @param {Object} product - Product from PPD
   * @param {Object} variant - Variant from PPD
   * @param {string} view - View type ('front' | 'back')
   * @returns {Array<string>} Array of image URLs
   */
  function getImagesForView(product, variant, view) {
    // Define configurator image keys once at top (DRY principle)
    const CONFIGURATOR_IMAGE_KEYS = {
      FRONT: 'configurator_front_image',
      BACK: 'configurator_back_image',
    };

    try {
      // Priority order: composite images > material images > product images > configurator images
      const imageKey = view === 'front' ? 'frontImages' : 'backImages';
      const configuratorImageKey =
        view === 'front'
          ? CONFIGURATOR_IMAGE_KEYS.FRONT
          : CONFIGURATOR_IMAGE_KEYS.BACK;

      // Check variant-level composite images first
      if (variant?.compositeImages?.[imageKey]?.length > 0) {
        return variant.compositeImages[imageKey];
      }

      // Check variant-level material images
      if (variant?.materialImages?.[imageKey]?.length > 0) {
        return variant.materialImages[imageKey];
      }

      // Check product-level images
      if (product?.[imageKey]?.length > 0) {
        return product[imageKey];
      }

      // 🔥 CRITICAL FIX: Check variant-level configurator images (direct property from Shopify API)
      // This is where the actual door carcass images are stored!
      if (variant?.[configuratorImageKey]) {
        // Normalize protocol-relative URLs to https:// so they pass the img-src https: CSP directive
        // on both production (HTTPS) and localhost dev (HTTP).
        // Pattern #17: Sanitize URL to prevent XSS via javascript:/data: URI injection
        const raw = variant[configuratorImageKey];
        const src = sanitizeImageUrl(
          raw.startsWith('//') ? 'https:' + raw : raw
        );
        return src ? [{ src, alt: variant.title }] : [];
      }

      // 🔥 FALLBACK: Check product-level configurator images (for single-variant products)
      // Single-variant products may store configurator images at product level, not variant level
      // Guard ensures this only applies to single-variant products to prevent ambiguity.
      if (product?.variants?.length <= 1 && product?.[configuratorImageKey]) {
        // Pattern #17: Sanitize URL to prevent XSS via javascript:/data: URI injection
        const raw = product[configuratorImageKey];
        const src = sanitizeImageUrl(
          raw.startsWith('//') ? 'https:' + raw : raw
        );
        return src ? [{ src, alt: product.title }] : [];
      }

      // 🛡️ GUARD: Single-view configurator products should NOT get thumbnail fallback for missing view
      // Determine product state first (declarative approach)
      // Check BOTH variant-level AND product-level (fallback for single-variant products)
      const hasFrontImage =
        !!variant?.[CONFIGURATOR_IMAGE_KEYS.FRONT] ||
        !!product?.[CONFIGURATOR_IMAGE_KEYS.FRONT];
      const hasBackImage =
        !!variant?.[CONFIGURATOR_IMAGE_KEYS.BACK] ||
        !!product?.[CONFIGURATOR_IMAGE_KEYS.BACK];
      const isFrontOnly = hasFrontImage && !hasBackImage;
      const isBackOnly = hasBackImage && !hasFrontImage;

      // Apply guard rule: skip layer creation for missing view on single-view products
      if (
        (view === 'back' && isFrontOnly) ||
        (view === 'front' && isBackOnly)
      ) {
        const reason = isFrontOnly
          ? 'front-only (has front, no back)'
          : 'back-only (has back, no front)';
        isDev() &&
          console.log(
            `[ImageLayerCoordinator] Product is ${reason} — skipping ${view} layer`,
            { productId: product?.id, variantId: variant?.id }
          );
        return [];
      }

      // 🔥 NO THUMBNAIL FALLBACK! Must have designated front/back image or nothing.
      // If we reach here, no designated image was found for this view.
      isDev() &&
        console.log(
          `[ImageLayerCoordinator] No designated ${view} image found - skipping layer`,
          { productId: product?.id, variantId: variant?.id }
        );
      return [];
    } catch (error) {
      isDev() &&
        console.error('[ImageLayerCoordinator] Error getting images:', error);
      return [];
    }
  }

  /**
   * Detect if auto-toggle is needed based on image availability
   *
   * @param {Object} product - Product from PPD
   * @param {Object} variant - Variant from PPD
   * @param {string} currentView - Current view ('front' | 'back')
   * @returns {string|null} Target view if toggle needed, null otherwise
   */
  /**
   * May THIS category's image availability move the camera right now?
   *
   * 🚨 THE BUG THIS EXISTS FOR. detectAutoToggleNeeded() answers "does this
   * product have art on only one side", which is a fact about a product and
   * says nothing about what the customer is looking at. It is evaluated once
   * per applied selection -- and a single click in apo_style cascades ~15
   * further applies, none of which the customer made. Measured on the dev
   * server, clicking Door Backing -> Flat Behind produced, after the customer's
   * own correct flip to back:
   *
   *   AUTO_TOGGLE -> back   only_back_image_available   Back Entablature
   *   AUTO_TOGGLE -> front  only_front_image_available  Front Casing   <- won
   *
   * Neither category was clicked. Whichever cascade-applied category finished
   * rendering LAST won the camera, so the outcome was decided by async
   * completion order -- which is why the same click could look right or wrong.
   *
   * The rule: while the customer has an explicit side intent, only the category
   * they actually acted on may move the camera. Every other category's art is
   * still rendered for both sides exactly as before; only the CAMERA is held.
   *
   * With no intent recorded (side-neutral category, bootstrap, `?config=`
   * restore) this returns true and behaviour is unchanged from before the fix.
   *
   * @param {string} categoryId - the category of the event being handled
   * @returns {boolean}
   */
  function mayAutoToggleForCategory(categoryId) {
    // Read through the conductor (CCP delegation), never from our own guess.
    // Optional-chained throughout: a missing conductor must degrade to the
    // previous behaviour, not throw out of a render pipeline.
    const intent =
      window.MurphyDoorConfigurator?.main?.getActiveViewIntent?.() || null;
    if (!intent) return true;

    if (intent.categoryId === categoryId) return true;

    isDev() &&
      console.log(
        `[ImageLayerCoordinator] Auto-toggle suppressed for "${categoryId}" -- customer intent is ${intent.view} from "${intent.categoryId}"`
      );
    return false;
  }

  function detectAutoToggleNeeded(product, variant, currentView) {
    try {
      const hasFront = getImagesForView(product, variant, 'front').length > 0;
      const hasBack = getImagesForView(product, variant, 'back').length > 0;

      // Both views available → no auto-toggle
      if (hasFront && hasBack) {
        isDev() &&
          console.log(
            '[ImageLayerCoordinator] Both views available - no auto-toggle'
          );
        return null;
      }

      // Neither view available → no auto-toggle
      if (!hasFront && !hasBack) {
        isDev() &&
          console.log(
            '[ImageLayerCoordinator] No images available - no auto-toggle'
          );
        return null;
      }

      // Only front available + currently showing back → toggle to front
      if (hasFront && !hasBack && currentView === 'back') {
        isDev() &&
          console.log(
            '[ImageLayerCoordinator] Only front available, auto-toggle to front'
          );
        return 'front';
      }

      // Only back available + currently showing front → toggle to back
      if (hasBack && !hasFront && currentView === 'front') {
        isDev() &&
          console.log(
            '[ImageLayerCoordinator] Only back available, auto-toggle to back'
          );
        return 'back';
      }

      // Already showing correct view → no toggle needed
      return null;
    } catch (error) {
      isDev() &&
        console.error(
          '[ImageLayerCoordinator] Auto-toggle detection failed:',
          error
        );
      return null;
    }
  }

  /**
   * Request category media z-index from conductor (PPD-First O(1) lookup)
   * Uses conductor.getCategoryClassification() to access PPD category metadata
   *
   * @param {string} categoryId - Category ID
   * @returns {number} Category media z-index (0 if not found)
   */
  function requestCategoryMediaZIndex(categoryId) {
    if (!conductor) {
      isDev() &&
        console.warn(
          '[ImageLayerCoordinator] Conductor not available for z-index lookup'
        );
      return 0;
    }

    try {
      const categoryData = conductor.getCategoryClassification(categoryId);
      const mediaZIndex = categoryData?.mediaZIndex || 0;

      isDev() &&
        console.log(
          `[ImageLayerCoordinator] Z-Index lookup: category="${categoryId}" → mediaZIndex=${mediaZIndex}`
        );

      return mediaZIndex;
    } catch (error) {
      isDev() &&
        console.error(
          `[ImageLayerCoordinator] Failed to get mediaZIndex for category "${categoryId}":`,
          error
        );
      return 0;
    }
  }

  /**
   * Request the category's display order (its tab position in the configurator
   * flow) from the conductor. The gallery uses it as the STACKING TIEBREAK for
   * layers whose computed z-index is equal — `media_z_index` is merchant-authored
   * per tab and two tabs can share a value (Shelf Style and Wood & Finish both
   * carried 10 on 2026-09-18, which left the shelves' paint order to whichever
   * selection happened to apply last: correct on a hand-built door, wrong on the
   * same door restored from a ?config= link). A later tab stacks above an
   * earlier one, which is the hand-built result made deterministic.
   *
   * @param {string} categoryId - Category ID
   * @returns {number|null} Display order, or null when the conductor cannot say
   *   (the gallery then falls back to its pure z-index behaviour for that layer)
   */
  function requestCategoryStackOrder(categoryId) {
    if (!conductor || typeof conductor.getCategoryDisplayOrder !== 'function') {
      return null;
    }
    try {
      const order = conductor.getCategoryDisplayOrder(categoryId);
      return Number.isFinite(order) ? order : null;
    } catch (error) {
      isDev() &&
        console.error(
          `[ImageLayerCoordinator] Failed to get display order for category "${categoryId}":`,
          error
        );
      return null;
    }
  }

  /**
   * Calculate computed z-index for a layer based on base + category mediaZIndex
   * BROWSING layers: BROWSING_BASE (1000) + mediaZIndex
   * APPLIED layers: APPLIED_BASE (500) + mediaZIndex
   *
   * @param {string} layerType - 'browsing' | 'applied'
   * @param {string} categoryId - Category ID for PPD lookup
   * @returns {number} Computed z-index value
   */
  function calculateLayerZIndex(layerType, categoryId) {
    const base =
      layerType === 'browsing' ? Z_INDEX.BROWSING_BASE : Z_INDEX.APPLIED_BASE;
    const mediaZIndex = requestCategoryMediaZIndex(categoryId);
    const computedZIndex = base + mediaZIndex;

    isDev() &&
      console.log(
        `[ImageLayerCoordinator] Z-Index calc: ${layerType} layer for "${categoryId}" = ${base} (base) + ${mediaZIndex} (mediaZIndex) = ${computedZIndex}`
      );

    return computedZIndex;
  }

  /**
   * Request product data from conductor (PATTERN 6: Strict PPD Dependency)
   *
   * Uses a multi-strategy resolution to handle both raw Shopify products (numeric IDs)
   * and composite products (string IDs like "composite-bottom-pocket-standard-shelves").
   *
   * When a shelf is auto-applied via a forbidden AUTO_APPLY_OPTION rule, the productId
   * in the SELECTION_APPLIED event is the composite product ID — conductor.getProductById()
   * only searches ppd.products (numeric keys) and returns null for these, causing
   * getImagesForView(null, variant, view) to silently skip the gallery update.
   *
   * Resolution strategy — variantId-first for guaranteed product/variant consistency:
   *
   * When both productId and variantId are available, variantId is the most specific
   * identifier and is used as the primary source of truth. This guarantees the
   * returned product and variant always belong to the same PPD entry, preventing
   * mismatched pairs when productId and variantId originate from different sources
   * (e.g. auto-apply events that carry a composite productId with a raw variantId).
   *
   * Resolution order:
   * 1. variantId → getVariantById + getProductByVariantId  (primary — consistent pair)
   * 2. productId → getProductById                          (raw Shopify numeric IDs)
   * 3. productId → getVirtualizedProductById               (composite string IDs)
   *
   * @param {string} productId - Product ID (may be raw numeric or composite string)
   * @param {string} [variantId] - Variant ID (preferred source of truth when available)
   * @returns {Promise<{product: Object, variant: Object}>}
   */
  async function requestProductDataFromConductor(productId, variantId) {
    if (!conductor) {
      throw new Error('Conductor not available');
    }

    try {
      let product = null;
      let variant = null;

      // Strategy 1: variantId-first — guarantees a consistent product/variant pair
      // Use this as the primary path when variantId is available; the variant map
      // works for both raw Shopify variants and composite/synthetic variant IDs.
      if (variantId) {
        variant = conductor.getVariantById(variantId);
        if (typeof conductor.getProductByVariantId === 'function') {
          product = conductor.getProductByVariantId(variantId);
        }
      }

      // Strategy 2: productId numeric lookup — fast path for raw Shopify products
      // Only used when variantId resolution didn't yield a product.
      if (!product && productId) {
        product = conductor.getProductById(productId);
      }

      // Strategy 3: Virtualized product lookup — handles composite product string IDs
      // like "composite-bottom-pocket-standard-shelves" from auto-apply flows.
      if (
        !product &&
        productId &&
        typeof conductor.getVirtualizedProductById === 'function'
      ) {
        product = conductor.getVirtualizedProductById(productId);
        if (product) {
          isDev() &&
            console.log(
              `[ImageLayerCoordinator] Product resolved via getVirtualizedProductById for "${productId}"`
            );
        }
      }

      if (!product) {
        // Publish a failed event so this resolution failure is observable in production,
        // not just silently swallowed behind isDev() guards.
        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_FAILED,
          {
            reason: 'product_resolution_failed',
            productId,
            variantId,
            strategiesAttempted: [
              'getProductByVariantId',
              'getProductById',
              'getVirtualizedProductById',
            ],
            timestamp: Date.now(),
          },
          'requestProductDataFromConductor'
        );
        throw new Error(
          `Could not resolve product for productId="${productId}" variantId="${variantId}" — all 3 resolution strategies failed`
        );
      }

      // Fall back to first product variant if variantId was not provided or not found.
      // Inset-nudge note: on a composite, variants[0] carries the FIRST member's
      // sourceProductIsInset, which may not match the actually-selected member — a
      // theoretical mis-resolution if getVariantById ever fails on an otherwise-resolvable
      // product. detectInsetFromResolved then falls through to product.is_inset (correct for
      // non-composites; undefined for composites → no nudge). Low-probability, and strictly
      // more robust than the pre-metafield path which had product.is_inset only.
      const resolvedVariant = variant ?? product?.variants?.[0] ?? null;

      return { product, variant: resolvedVariant };
    } catch (error) {
      isDev() &&
        console.error(
          '[ImageLayerCoordinator] Failed to get product data from conductor:',
          error
        );
      throw error;
    }
  }

  /**
   * Request current state from conductor
   *
   * @returns {Promise<Object>} Current state
   */
  async function requestStateFromConductor() {
    if (!conductor) {
      throw new Error('Conductor not available');
    }

    try {
      return conductor.getStateMaps();
    } catch (error) {
      isDev() &&
        console.error(
          '[ImageLayerCoordinator] Failed to get state from conductor:',
          error
        );
      throw error;
    }
  }

  /**
   * Request current view from conductor
   *
   * @returns {Promise<string>} Current view ('front' | 'back')
   */
  async function requestCurrentViewFromConductor() {
    if (!conductor) {
      throw new Error('Conductor not available');
    }

    try {
      const state = conductor.getStateMaps();
      return state?.viewState?.currentView || 'front';
    } catch (error) {
      isDev() &&
        console.error(
          '[ImageLayerCoordinator] Failed to get current view from conductor:',
          error
        );
      return 'front'; // Safe default
    }
  }

  // ============================================================================
  // PATTERN 17: XSS PREVENTION — IMAGE URL VALIDATION
  // ============================================================================

  /**
   * Validate and sanitize an image URL to prevent XSS via javascript: or data: URI injection.
   * Only allows https:, http:, and protocol-relative (//) URLs.
   *
   * @param {string} url - Image URL to validate
   * @returns {string|null} Sanitized URL or null if invalid/dangerous
   */
  function sanitizeImageUrl(url) {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();
    // Allow https://, http://, and protocol-relative //
    if (
      trimmed.startsWith('https://') ||
      trimmed.startsWith('http://') ||
      trimmed.startsWith('//')
    ) {
      return trimmed;
    }
    isDev() &&
      console.warn(
        '[ImageLayerCoordinator] Blocked potentially unsafe image URL:',
        trimmed
      );
    return null;
  }

  // ============================================================================
  // VALIDATION HELPERS
  // ============================================================================

  /**
   * Validate view toggle event data
   *
   * @param {Object} eventData - Event data to validate
   * @returns {{isValid: boolean, errors: Array<string>, warnings: Array<string>}}
   */
  function validateViewToggleData(eventData) {
    const errors = [];
    const warnings = [];

    if (!eventData) {
      errors.push('Event data is null or undefined');
      return { isValid: false, errors, warnings };
    }

    if (!eventData.view || !['front', 'back'].includes(eventData.view)) {
      errors.push(
        `Invalid view: ${eventData.view} (must be 'front' or 'back')`
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Validate selection event data
   *
   * @param {Object} eventData - Event data to validate
   * @returns {{isValid: boolean, errors: Array<string>, warnings: Array<string>}}
   */
  function validateSelectionData(eventData) {
    const errors = [];
    const warnings = [];

    if (!eventData) {
      errors.push('Event data is null or undefined');
      return { isValid: false, errors, warnings };
    }

    if (!eventData.categoryId) {
      errors.push('Missing categoryId');
    }

    if (!eventData.productId) {
      errors.push('Missing productId');
    }

    // variantId is optional (single-variant products don't have it)
    if (!eventData.variantId) {
      warnings.push('No variantId - assuming single-variant product');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  // ============================================================================
  // URL RESTORATION HANDLER
  // ============================================================================

  /**
   * Cascade contexts whose COMPLETED triggers a FULL re-render of every applied
   * layer. Both apply a batch through BUSINESS_CASCADE_APPLY_URL_SELECTIONS:
   * - 'url_restoration': ?config= restore (selections land before this module
   *   is initialized - the original timing race)
   * - 'build_preset': "Build this" apply
   *   (md-config-support-build-preset.js). Its picks are auto-applied like a
   *   ?config= restore (they were manual until 2026-09-30, when the
   *   normal-cascade safety net below - which only re-renders
   *   autoAppliedCategories - repainted NOTHING); the full re-render stays so
   *   every applied layer, not just this batch's, is repainted.
   */
  const FULL_RERENDER_CONTEXTS = Object.freeze([
    'url_restoration',
    'build_preset',
  ]);

  function isFullRerenderContext(context) {
    return (
      typeof context === 'string' && FULL_RERENDER_CONTEXTS.indexOf(context) !== -1
    );
  }

  /**
   * Handle URL restoration completion - re-render all applied layers
   *
   * PATTERN 4: Event Consumer Handler
   * PATTERN 14: Try/Catch + Failed Events
   *
   * When URL params load, selection:applied events fire BEFORE this module
   * is initialized (timing race). This handler catches the cascade completion
   * event and re-renders all applied image layers from current state.
   *
   * Only triggers for url_restoration context to avoid interfering with
   * normal cascade auto-selection flows.
   *
   * @param {Object} eventData - Cascade completion event data
   * @param {string} eventData.context - Must be 'url_restoration' to trigger
   * @param {number} eventData.totalSteps - Number of selections applied
   * @param {Array<string>} eventData.autoAppliedCategories - Categories that were applied
   */
  async function handleUrlRestorationCompleted(eventData) {
    // Only contexts that apply a whole batch through the URL-restoration
    // transport (URL restore, "Build this" preset apply + its undo).
    if (!isFullRerenderContext(eventData && eventData.context)) {
      return;
    }

    // url_restoration keeps its historical operation name (dashboards/tests).
    // build_preset's is a shared constant: md-config-ui-build-preset.js hides
    // its progress overlay on exactly this operation.
    let operationName = `${eventData.context}_layer_sync`;
    if (eventData.context === 'url_restoration') {
      operationName = 'url_restoration_layer_sync';
    } else if (
      eventData.context === 'build_preset' &&
      CONSTANTS.BUILD_PRESET_LAYER_SYNC_OPERATION
    ) {
      operationName = CONSTANTS.BUILD_PRESET_LAYER_SYNC_OPERATION;
    }
    const operationId = `${eventData.context}_${Date.now()}`;
    const startTime = performance.now();

    try {
      // STAGE 1: START
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_STARTED,
        {
          operationId,
          operation: operationName,
          totalSteps: eventData.totalSteps,
          autoAppliedCategories: eventData.autoAppliedCategories,
          timestamp: Date.now(),
        },
        'handleUrlRestorationCompleted'
      );

      // Get current applied selections from conductor state
      const stateMaps = await requestStateFromConductor();

      if (
        !stateMaps.appliedSelections ||
        stateMaps.appliedSelections.size === 0
      ) {
        isDev() &&
          console.log(
            '[ImageLayerCoordinator] URL restoration: No applied selections to render layers for'
          );
        return;
      }

      isDev() &&
        console.log(
          `[ImageLayerCoordinator] URL restoration: Re-rendering layers for ${stateMaps.appliedSelections.size} applied selections`
        );

      // Re-render applied layers for ALL applied selections (both views)
      for (const [
        categoryId,
        selectionData,
      ] of stateMaps.appliedSelections.entries()) {
        try {
          await renderAppliedLayer(categoryId, 'front', selectionData);
          await renderAppliedLayer(categoryId, 'back', selectionData);
        } catch (layerError) {
          isDev() &&
            console.error(
              `[ImageLayerCoordinator] URL restoration: Failed to render layers for category "${categoryId}":`,
              layerError
            );
          // Continue with remaining categories even if one fails
        }
      }

      // STAGE 5: SUCCESS
      const duration = performance.now() - startTime;

      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_COMPLETED,
        {
          operationId,
          operation: operationName,
          categoriesRendered: stateMaps.appliedSelections.size,
          duration: duration.toFixed(2),
          timestamp: Date.now(),
        },
        'handleUrlRestorationCompleted'
      );

      // Performance tracking
      if (duration > PERFORMANCE_THRESHOLDS.VIEW_COORDINATION) {
        isDev() &&
          console.warn(
            `[ImageLayerCoordinator] URL restoration layer sync slow: ${duration.toFixed(2)}ms`
          );
        publishEvent(
          CONSTANTS.EVENTS.PERFORMANCE_IMAGE_LAYER_OPERATION_SLOW,
          {
            operationId,
            operation: operationName,
            duration: duration.toFixed(2),
            threshold: PERFORMANCE_THRESHOLDS.VIEW_COORDINATION,
          },
          'handleUrlRestorationCompleted'
        );
      }

      isDev() &&
        console.log(
          `[ImageLayerCoordinator] URL restoration layer sync complete: ${stateMaps.appliedSelections.size} categories rendered in ${duration.toFixed(2)}ms`
        );
    } catch (error) {
      isDev() &&
        console.error(
          '[ImageLayerCoordinator] URL restoration layer sync failed:',
          error
        );

      // STAGE 5: FAILED
      publishEvent(
        CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_FAILED,
        {
          operationId,
          operation: operationName,
          error: error.message,
          stack: error.stack,
          timestamp: Date.now(),
        },
        'handleUrlRestorationCompleted'
      );
    }
  }

  // ============================================================================
  // PATTERN 4: HANDLER — CASCADE COMPLETION ROUTER
  // ============================================================================

  /**
   * Handle cascade auto-selection completion — routes to URL restoration or normal cascade safety net
   *
   * PATTERN 4: Event Consumer Handler
   *
   * Routes to the appropriate handler based on cascade context:
   * - 'url_restoration': delegates to handleUrlRestorationCompleted for full re-render of all layers
   * - normal cascade: re-renders only auto-applied categories as a belt-and-suspenders safety net
   *
   * This is the named function queued in pendingCalls so the pre-bootstrap path correctly
   * handles both url_restoration AND normal cascade events.
   *
   * @param {Object} eventData - Cascade completion event data
   * @param {string} [eventData.context] - 'url_restoration' for URL param cascades; absent for normal
   * @param {Array<string>} eventData.autoAppliedCategories - Categories that were auto-applied
   */
  async function handleCascadeCompleted(eventData) {
    // URL restoration / build preset path: full re-render of all applied layers
    if (isFullRerenderContext(eventData && eventData.context)) {
      handleUrlRestorationCompleted(eventData);
      return;
    }

    // Normal cascade safety net: re-render layers for each auto-applied category
    // using state from conductor (since cascade event only carries category IDs)
    const autoApplied = eventData.autoAppliedCategories;
    if (!autoApplied || autoApplied.length === 0) {
      return;
    }

    try {
      const stateMaps = await requestStateFromConductor();
      if (
        !stateMaps.appliedSelections ||
        stateMaps.appliedSelections.size === 0
      ) {
        return;
      }

      for (let i = 0; i < autoApplied.length; i++) {
        const categoryId = autoApplied[i];
        const selectionData = stateMaps.appliedSelections.get(categoryId);
        if (
          selectionData &&
          selectionData.productId &&
          selectionData.variantId
        ) {
          isDev() &&
            console.log(
              '[ImageLayerCoordinator] Safety-net: re-rendering applied layer after CASCADE_AUTO_SELECTION_COMPLETED',
              {
                categoryId: categoryId,
                productId: selectionData.productId,
                variantId: selectionData.variantId,
              }
            );
          try {
            await handleAppliedSelection({
              categoryId: categoryId,
              productId: selectionData.productId,
              variantId: selectionData.variantId,
            });
          } catch (categoryError) {
            isDev() &&
              console.error(
                `[ImageLayerCoordinator] Cascade safety-net: failed to re-render layer for category "${categoryId}":`,
                categoryError
              );
            // Continue with remaining categories even if one fails
          }
        }
      }
    } catch (error) {
      isDev() &&
        console.error(
          '[ImageLayerCoordinator] Cascade safety-net failed to retrieve state:',
          error
        );
    }
  }

  // ============================================================================
  // PATTERN 15: SUBSCRIPTION MANAGEMENT & CLEANUP
  // ============================================================================

  /**
   * Subscribe and track for cleanup (PATTERN 15)
   *
   * @param {string} eventType - Event type to subscribe to
   * @param {Function} handler - Event handler function
   * @returns {Function} Unsubscribe function
   */
  function subscribeWithCleanup(eventType, handler) {
    if (!eventBus) {
      isDev() &&
        console.warn(
          `[ImageLayerCoordinator] Cannot subscribe to ${eventType} - EventBus not available`
        );
      return () => {};
    }

    const unsubscribe = eventBus.subscribe(eventType, handler);
    cleanupHandlers.push(unsubscribe);
    return unsubscribe;
  }

  /**
   * Setup event subscriptions (PATTERN 15: Cleanup tracking)
   *
   * @returns {void}
   */
  function setupEventSubscriptions() {
    // Subscribe to view toggle events (inline bootstrap guard)
    subscribeWithCleanup(CONSTANTS.EVENTS.UI_VIEW_TOGGLED, (eventData) => {
      if (!bootstrapComplete) {
        isDev() &&
          console.log(
            '[ImageLayerCoordinator] Queueing view_toggled until bootstrap complete'
          );
        pendingCalls.push({
          fn: handleViewToggled,
          context: null,
          args: [eventData],
        });
        return;
      }
      handleViewToggled(eventData);
    });

    // Subscribe to BROWSING state selections (inline bootstrap guard)
    subscribeWithCleanup(CONSTANTS.EVENTS.SELECTION_BROWSING, (eventData) => {
      if (!bootstrapComplete) {
        isDev() &&
          console.log(
            '[ImageLayerCoordinator] Queueing browsing_selection until bootstrap complete'
          );
        pendingCalls.push({
          fn: handleBrowsingSelection,
          context: null,
          args: [eventData],
        });
        return;
      }
      handleBrowsingSelection(eventData);
    });

    // Subscribe to APPLIED state selections (inline bootstrap guard)
    subscribeWithCleanup(CONSTANTS.EVENTS.SELECTION_APPLIED, (eventData) => {
      if (!bootstrapComplete) {
        isDev() &&
          console.log(
            '[ImageLayerCoordinator] Queueing applied_selection until bootstrap complete'
          );
        pendingCalls.push({
          fn: handleAppliedSelection,
          context: null,
          args: [eventData],
        });
        return;
      }
      handleAppliedSelection(eventData);
    });

    // Subscribe to CASCADE_AUTO_SELECTION_COMPLETED for TWO purposes:
    // 1. URL restoration: re-renders all applied layers from current state (context === 'url_restoration')
    // 2. Normal cascade safety net: re-renders layers for auto-applied categories that may have
    //    missed their SELECTION_APPLIED image layer render (e.g., composite bottom pocket variant IDs
    //    that requestProductDataFromConductor intermittently fails to resolve).
    //    Follows the same belt-and-suspenders pattern as FORBIDDEN_AUTO_APPLY_COMPLETED below.
    subscribeWithCleanup(
      CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
      (eventData) => {
        if (!bootstrapComplete) {
          isDev() &&
            console.log(
              '[ImageLayerCoordinator] Queueing cascade_completed until bootstrap complete'
            );
          pendingCalls.push({
            fn: handleCascadeCompleted,
            context: null,
            args: [eventData],
          });
          return;
        }
        handleCascadeCompleted(eventData);
      }
    );

    // "Build this" settled repaint. The build preset module publishes APPLIED /
    // PARTIAL only after the configurator has gone idle (nested tiers
    // and composites land AFTER the first context-tagged COMPLETED - footgun
    // "nested-tier + composite restore settle late"), so repainting here paints
    // the SETTLED door. Idempotent with the COMPLETED re-render above.
    [
      CONSTANTS.EVENTS.BUSINESS_BUILD_PRESET_APPLIED,
      CONSTANTS.EVENTS.BUSINESS_BUILD_PRESET_PARTIAL,
    ].forEach((eventType) => {
      if (!eventType) return;
      subscribeWithCleanup(eventType, (eventData) => {
        const settled = {
          context: 'build_preset',
          handle: eventData && eventData.handle,
        };
        if (!bootstrapComplete) {
          pendingCalls.push({
            fn: handleUrlRestorationCompleted,
            context: null,
            args: [settled],
          });
          return;
        }
        handleUrlRestorationCompleted(settled);
      });
    });

    // Subscribe to FORBIDDEN_AUTO_APPLY_COMPLETED as a guaranteed-delivery safety net.
    //
    // The primary gallery update path is: conductor.APPLY_SELECTION →
    // stateManager publishes SELECTION_APPLIED → handleAppliedSelection renders layer.
    // That path calls requestProductDataFromConductor(productId, variantId). For auto-applied
    // composite shelf products, productId is a string like "composite-bottom-pocket-standard-shelves"
    // that getProductById() can't resolve (only numeric IDs). The fallback strategies added to
    // requestProductDataFromConductor handle this, but this subscription is a belt-and-suspenders
    // guarantee: if SELECTION_APPLIED somehow misses or getImagesForView returns empty for the
    // target product, we force-render the applied layer here using data from the completed event.
    //
    // Re-rendering an already-correct layer is idempotent (same images, same z-index), so
    // a double-render is a safe no-op. The cascade coordinator fires this event AFTER
    // conductor.handleStateChange('APPLY_SELECTION') has fully completed, so state is guaranteed
    // to be committed when this fires.
    subscribeWithCleanup(
      CONSTANTS.EVENTS.FORBIDDEN_AUTO_APPLY_COMPLETED,
      (eventData) => {
        if (!bootstrapComplete) {
          isDev() &&
            console.log(
              '[ImageLayerCoordinator] Queueing forbidden_auto_apply_completed until bootstrap complete'
            );
          // INTENTIONAL PATTERN DEVIATION: Unlike other handlers that queue with full eventData,
          // this queues handleForbiddenAutoApplyFromCommittedState with only targetCategoryId.
          // Reason: by the time the queue is processed after bootstrap, the original eventData
          // payload (with synthetic variant IDs) may be stale and will intermittently fail PPD
          // map lookups. Reading from committed appliedSelections state is always reliable.
          // Do NOT change this to pass eventData — it would re-introduce the intermittent bug.
          const queuedCategoryId = eventData.targetCategoryId;
          if (queuedCategoryId) {
            pendingCalls.push({
              fn: handleForbiddenAutoApplyFromCommittedState,
              context: null,
              args: [queuedCategoryId],
            });
          }
          return;
        }
        const { targetCategoryId } = eventData;
        if (!targetCategoryId) {
          return;
        }
        // Read from committed appliedSelections state instead of event payload IDs.
        // Synthetic variant IDs in the payload intermittently fail PPD map lookups;
        // committed state is guaranteed to be correct when this event fires.
        handleForbiddenAutoApplyFromCommittedState(targetCategoryId);
      }
    );

    // Subscribe to SELECTION_USER_UNAPPLY_COMPLETED to remove ghost applied layers.
    //
    // When a user clicks × to unapply, the conductor clears state and publishes
    // SELECTION_USER_UNAPPLY_COMPLETED. This bridges that event to UI_APPLIED_LAYER_REMOVE
    // so media-gallery.js removes the applied layer DOM elements. Without this, applied
    // layers persist as ghost elements after unapply.
    subscribeWithCleanup(
      CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
      (eventData) => {
        if (!bootstrapComplete) {
          isDev() &&
            console.log(
              '[ImageLayerCoordinator] Queueing unapply_completed until bootstrap complete'
            );
          pendingCalls.push({
            fn: handleUnapplyCompleted,
            context: null,
            args: [eventData],
          });
          return;
        }
        handleUnapplyCompleted(eventData);
      }
    );

    // NOTE: Bootstrap event subscription moved to module load time (top-level)
    // to avoid race condition where event fires before initialize() is called

    isDev() &&
      console.log(
        `[ImageLayerCoordinator] Subscribed to ${cleanupHandlers.length} events`
      );
  }

  // ============================================================================
  // PATTERN 16: INITIALIZATION WITH GUARD
  // ============================================================================

  /**
   * Initialize the Image Layer Coordinator
   * PATTERN 16: Initialization guard prevents double initialization
   *
   * @param {Object} conductorInstance - Conductor instance for delegation
   * @returns {Promise<boolean>} Success status
   */
  async function initialize(conductorInstance) {
    // Guard: Already initialized
    if (isInitialized) {
      isDev() &&
        console.warn('[ImageLayerCoordinator] Already initialized - skipping');
      return true;
    }

    // Guard: Initialization in progress
    if (initializationPromise) {
      isDev() &&
        console.log(
          '[ImageLayerCoordinator] Initialization in progress - waiting'
        );
      return initializationPromise;
    }

    // Create initialization promise
    initializationPromise = (async () => {
      try {
        isDev() &&
          console.log('[ImageLayerCoordinator] 🚀 Starting initialization...');

        // Validate conductor
        if (!conductorInstance) {
          throw new Error('Conductor instance is required');
        }

        conductor = conductorInstance;
        eventBus = window.MurphyDoorConfigurator.CoreEventBus;

        if (!eventBus) {
          throw new Error('CoreEventBus not available');
        }

        // Setup event subscriptions (PATTERN 15)
        setupEventSubscriptions();

        // Mark as initialized
        isInitialized = true;

        // Publish ready event
        publishEvent(
          CONSTANTS.EVENTS.MODULE_IMAGE_LAYER_COORDINATOR_READY,
          {
            timestamp: Date.now(),
            subscriptionCount: cleanupHandlers.length,
          },
          'initialize'
        );

        isDev() &&
          console.log('[ImageLayerCoordinator] ✅ Initialization complete');
        return true;
      } catch (error) {
        isDev() &&
          console.error(
            '[ImageLayerCoordinator] Initialization failed:',
            error
          );

        publishEvent(
          CONSTANTS.EVENTS.BUSINESS_IMAGE_LAYER_OPERATION_FAILED,
          {
            error: error.message,
            operation: 'initialization',
            stack: error.stack,
          },
          'initialize'
        );

        throw error;
      } finally {
        initializationPromise = null;
      }
    })();

    return initializationPromise;
  }

  // ============================================================================
  // PATTERN 15: CLEANUP IMPLEMENTATION
  // ============================================================================

  /**
   * Cleanup all subscriptions and resources
   *
   * @returns {Promise<void>}
   */
  async function cleanup() {
    isDev() &&
      console.log(
        `[ImageLayerCoordinator] 🧹 Cleaning up ${cleanupHandlers.length} subscriptions`
      );

    publishEvent(
      CONSTANTS.EVENTS.MODULE_IMAGE_LAYER_COORDINATOR_CLEANUP,
      {
        subscriptionCount: cleanupHandlers.length,
        timestamp: Date.now(),
      },
      'cleanup'
    );

    // Unsubscribe all handlers
    cleanupHandlers.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (error) {
        isDev() &&
          console.error('[ImageLayerCoordinator] Cleanup error:', error);
      }
    });

    cleanupHandlers = [];

    // Clear pending calls
    pendingCalls = [];

    // Reset state
    isInitialized = false;
    bootstrapComplete = false;
    conductor = null;
    eventBus = null;

    isDev() && console.log('[ImageLayerCoordinator] ✅ Cleanup complete');
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================
  window.MurphyDoorConfigurator.imageLayerCoordinator = {
    initialize,
    cleanup,
    handleViewToggled,
    handleBrowsingSelection,
    handleAppliedSelection,
    handleCascadeCompleted, // cascade COMPLETED router - exposed for unit testing
    handleUrlRestorationCompleted, // full re-render - exposed for unit testing
    isFullRerenderContext, // pure context gate - exposed for unit testing
    FULL_RERENDER_CONTEXTS,
    detectInsetFromResolved, // pure inset decision — exposed for unit testing
    resolveShiftFromRule, // pure rule→shift decision — exposed for unit testing
    parseShiftRule, // pure rule parse/validate — exposed for unit testing
    ruleAppliesToProduct, // pure product-scope check — exposed for unit testing
    PERFORMANCE_THRESHOLDS,
    Z_INDEX,
  };

  isDev() && console.log('[ImageLayerCoordinator] Module loaded');
})(window);
